// Dinamik raporlar — dogal dille rapor tanimlama.
//
// AKIS (sayfa da bunu gorsel olarak anlatiyor):
//   1. Yaz      → istegini normal Turkce cumleyle
//   2. Hazirla  → model SQL DEGIL yapilandirilmis sorgu uretir, sunucu
//                 katalogla dogrular; HENUZ CALISMAZ
//   3. Onayla   → ne yapilacagini okursun, sen baslatirsin
//   4. Kaydet   → sonraki calistirmalar modele hic ugramaz
//
// Yalnizca root erisir (rota `requiresRoot`): motor CRM'in ekran
// izinlerini degil dogrudan veritabanini okuyor.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, TextField, Button, Stack, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, InputAdornment, alpha, useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import BookmarkAddRoundedIcon from '@mui/icons-material/BookmarkAddRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import PageContainer from '../../components/PageContainer/PageContainer';
import DeleteConfirmModal from '../../components/DeleteConfirmModal/DeleteConfirmModal';
import reportService from '../../services/reportService';
import { useNotificationContext } from '../../providers/NotificationProvider';

const EXAMPLE_KEYS = ['noBonus', 'tag1502', 'signedThisYear'];

/** Adim basligi — akisin neresinde oldugumuzu gosterir. */
const StepLabel = ({ n, title, done, active }) => {
  const theme = useTheme();
  const color = done || active ? theme.palette.primary.main : theme.palette.text.disabled;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 800,
        color: done || active ? '#fff' : theme.palette.text.disabled,
        bgcolor: done || active ? color : alpha(theme.palette.text.disabled, 0.15),
      }}>
        {n}
      </Box>
      <Typography sx={{
        fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
        textTransform: 'uppercase', color,
      }}>
        {title}
      </Typography>
    </Stack>
  );
};

/** Ozet rakam kutucugu — sonuc basligindaki sayilar. */
const Stat = ({ icon: Icon, label, value, tone }) => {
  const theme = useTheme();
  const c = tone || theme.palette.text.secondary;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Icon sx={{ fontSize: 16, color: c }} />
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{value}</Box>
        {` ${label}`}
      </Typography>
    </Stack>
  );
};

const DynamicReportsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showNotification } = useNotificationContext();

  const [prompt, setPrompt] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [activeSaved, setActiveSaved] = useState(null);

  const [saved, setSaved] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadSaved = useCallback(async () => {
    try { setSaved(await reportService.list()); } catch { setSaved([]); }
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const step = useMemo(() => {
    if (result) return 3;
    if (draft) return 2;
    return 1;
  }, [draft, result]);

  const reset = () => { setDraft(null); setResult(null); setActiveSaved(null); setPrompt(''); };

  const handleDraft = async () => {
    const text = prompt.trim();
    if (!text) return;
    setDrafting(true); setDraft(null); setResult(null); setActiveSaved(null);
    try { setDraft(await reportService.draft(text)); }
    catch (err) { showNotification(err.message, 'error'); }
    finally { setDrafting(false); }
  };

  const handleRun = async (query) => {
    setRunning(true);
    try { setResult(await reportService.run(query)); }
    catch (err) { showNotification(err.message, 'error'); setResult(null); }
    finally { setRunning(false); }
  };

  const handleRunSaved = async (report) => {
    setRunning(true);
    setActiveSaved(report.id);
    setDraft({ query: report.queryJson, explanation: report.description || report.name });
    try { setResult(await reportService.runSaved(report.id)); }
    catch (err) { showNotification(err.message, 'error'); setResult(null); }
    finally { setRunning(false); }
  };

  const handleSave = async () => {
    const name = saveName.trim();
    if (!name || !draft?.query) return;
    try {
      await reportService.save({
        name, description: draft.explanation,
        sourcePrompt: prompt.trim() || null, query: draft.query,
      });
      showNotification(t('dynamicReports.toast.saved'), 'success');
      setSaveOpen(false); setSaveName(''); loadSaved();
    } catch (err) { showNotification(err.message, 'error'); }
  };

  /**
   * Sonucu Excel olarak indirir.
   *
   * Kaynak ekrandaki tablo DEGIL, sunucudan gelen ham satirlar: tablo
   * bicimlendirmesi (tire, kisaltma) dosyaya sizmasin. Basliklar
   * kullanicinin gordugu etiketler.
   */
  const handleExport = () => {
    if (!result?.rows?.length) return;
    const header = result.columns.map((c) => c.label);
    const body = result.rows.map((row) =>
      result.columns.map((c) => {
        const v = row[c.key];
        return v === null || v === undefined ? '' : v;
      }),
    );
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Rapor');

    // Dosya adi: kayitli rapor adi varsa o, yoksa tarih.
    const saved = activeSaved ? savedById.get(activeSaved)?.name : null;
    const stamp = new Date().toISOString().slice(0, 10);
    const safe = (saved || 'dinamik-rapor')
      .toLowerCase()
      .replace(/[^a-z0-9ğüşıöç]+/gi, '-')
      .replace(/^-|-$/g, '');
    XLSX.writeFile(book, `${safe}-${stamp}.xlsx`);
  };

  /**
   * Silme ONAY ister. Kayitli rapor kullanicinin yazip sakladigi bir
   * emek; tek tiklamayla ve geri alinamaz sekilde gitmemeli.
   */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await reportService.remove(deleteTarget.id);
      showNotification(t('dynamicReports.toast.deleted'), 'success');
      if (activeSaved === deleteTarget.id) reset();
      setDeleteTarget(null);
      loadSaved();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Sag kolon yalnizca gosterecek bir sey varken. Bos kart, sayfayi
  // dolu ama anlamsiz gosteriyordu.
  const hasSidebar = saved.length > 0;
  const savedById = useMemo(
    () => new Map(saved.map((r) => [r.id, r])),
    [saved],
  );

  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
  };

  return (
    <PageContainer currentPage={t('applications.titles.dynamicReports', 'Dinamik Raporlar')}>
      <Box sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
            {t('applications.titles.dynamicReports', 'Dinamik Raporlar')}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            {t('dynamicReports.subtitle')}
          </Typography>
        </Box>

      <Box sx={{
        display: 'grid', gap: 2.5,
        // Kayitli rapor yokken sag kolon bos bir kutu olarak duruyordu;
        // o durumda tek kolona dusuyoruz ve giris alani sayfayi kapliyor.
        gridTemplateColumns: hasSidebar
          ? { xs: '1fr', lg: '1fr 320px' }
          : '1fr',
        alignItems: 'start',
      }}>
        {/* ── SOL: akis ─────────────────────────────────────── */}
        {/* minWidth:0 SART: izgara ogesi varsayilan olarak icerigi kadar
            genisliyor. Onsuz genis tablo kolonu itiyor ve yatay kaydirma
            hic devreye girmiyordu. */}
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          {/* 1. adim — istek */}
          <Card sx={cardSx}>
            <Box sx={{
              px: 3, py: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <StepLabel n={1} title={t('dynamicReports.steps.write')} active={step === 1} done={step > 1} />
              {(draft || result) && (
                <Button size="small" startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={reset} sx={{ textTransform: 'none' }}>
                  {t('dynamicReports.newReport')}
                </Button>
              )}
            </Box>

            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth multiline minRows={2} maxRows={6}
                placeholder={t('dynamicReports.placeholder')}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  // Ctrl/Cmd+Enter ile gonder — uzun metinde fare aramayalim.
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleDraft();
                }}
                InputProps={{
                  sx: { borderRadius: 2, fontSize: 15, alignItems: 'flex-start' },
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mt: 1.5 }}>
                      <AutoAwesomeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                {EXAMPLE_KEYS.map((key) => (
                  <Button
                    key={key}
                    size="small"
                    variant="outlined"
                    onClick={() => setPrompt(t(`dynamicReports.examples.${key}.text`))}
                    sx={{
                      textTransform: 'none', borderRadius: 2,
                      fontSize: 12.5, fontWeight: 500,
                      color: 'text.secondary', borderColor: 'divider',
                      '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                    }}
                  >
                    {t(`dynamicReports.examples.${key}.label`)}
                  </Button>
                ))}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2.5 }}>
                <Button
                  variant="contained" size="large" disableElevation
                  startIcon={drafting
                    ? <CircularProgress size={16} color="inherit" />
                    : <AutoAwesomeIcon />}
                  onClick={handleDraft}
                  disabled={drafting || !prompt.trim()}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 3, fontWeight: 700 }}
                >
                  {drafting ? t('dynamicReports.preparing') : t('dynamicReports.prepare')}
                </Button>
                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                  ⌘/Ctrl + Enter
                </Typography>
              </Stack>

              {/* Guvenlik notu tek satir: ayri bir kart olarak sayfanin
                  yarisini kapliyordu. */}
              <Stack direction="row" alignItems="center" spacing={0.85} sx={{ mt: 2 }}>
                <LockRoundedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                  {t('dynamicReports.readOnlyNote')}
                </Typography>
              </Stack>
            </Box>
          </Card>

          {/* 2. adim — onay */}
          {draft && (
            <Card sx={cardSx}>
              <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <StepLabel n={2} title={t('dynamicReports.steps.review')} active={step === 2} done={step > 2} />
              </Box>
              <Box sx={{ p: 3 }}>
                <Typography sx={{ fontSize: 15, lineHeight: 1.6, mb: 2 }}>
                  {draft.explanation}
                </Typography>

                {draft.warnings?.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    {draft.warnings.join(' · ')}
                  </Alert>
                )}

                {/* Sorgunun ne dokundugu — kullanici SQL okumasin. */}
                <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                  <Chip  onClick={() => {
                    }} size="small" label={t('dynamicReports.source', { entity: draft.query?.entity })}
                    sx={{ borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main', fontWeight: 600 }} />
                  {draft.query?.filters?.length > 0 && (
                    <Chip  onClick={() => {
                    }} size="small" label={t('dynamicReports.conditionCount', { count: draft.query.filters.length })}
                      sx={{ borderRadius: 1.5 }} />
                  )}
                  {draft.query?.fields?.length > 0 && (
                    <Chip  onClick={() => {
                    }} size="small" label={t('dynamicReports.columnCount', { count: draft.query.fields.length })}
                      sx={{ borderRadius: 1.5 }} />
                  )}
                </Stack>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained" disableElevation
                    startIcon={running
                      ? <CircularProgress size={16} color="inherit" />
                      : <PlayArrowRoundedIcon />}
                    onClick={() => handleRun(draft.query)}
                    disabled={running}
                    sx={{ textTransform: 'none', borderRadius: 2, px: 3, fontWeight: 700 }}
                  >
                    {t('dynamicReports.run')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<BookmarkAddRoundedIcon />}
                    onClick={() => setSaveOpen(true)}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    {t('dynamicReports.save')}
                  </Button>
                </Stack>
              </Box>
            </Card>
          )}

          {/* 3. adim — sonuc */}
          {result && (
            <Card sx={cardSx}>
              <Box sx={{
                px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 1.5,
              }}>
                <StepLabel n={3} title={t('dynamicReports.steps.result')} active done />
                <Stack direction="row" spacing={2.5} alignItems="center">
                  <Stat
                    icon={TableRowsRoundedIcon}
                    value={result.truncated
                      ? `${result.rowCount} / ${result.totalCount}`
                      : result.rowCount}
                    label={t('dynamicReports.records')}
                  />
                  <Stat icon={TimerOutlinedIcon} value={`${result.durationMs}`} label="ms" />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownloadRoundedIcon sx={{ fontSize: 17 }} />}
                    onClick={handleExport}
                    disabled={!result.rows.length}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    {t('dynamicReports.export')}
                  </Button>
                </Stack>
              </Box>

              {result.truncated && (
                <Alert severity="info" sx={{ borderRadius: 0 }}>
                  {t('dynamicReports.truncated', { total: result.totalCount, shown: result.rowCount })}
                </Alert>
              )}

              <TableContainer sx={{ maxHeight: 560, overflowX: 'auto', maxWidth: '100%' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {result.columns.map((c) => (
                        <TableCell key={c.key} sx={{
                          fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap',
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          color: 'text.secondary',
                          bgcolor: theme.palette.background.paper,
                        }}>
                          {c.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={result.columns.length}
                          sx={{ textAlign: 'center', color: 'text.disabled', py: 6 }}>
                          {t('dynamicReports.noRows')}
                        </TableCell>
                      </TableRow>
                    ) : result.rows.map((row, i) => (
                      <TableRow key={i} hover>
                        {result.columns.map((c) => (
                          <TableCell key={c.key} sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                            {row[c.key] === null || row[c.key] === undefined
                              ? <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
                              : String(row[c.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </Stack>

        {/* ── SAG: kayitli raporlar ─────────────────────────── */}
        {hasSidebar && (
          <Card sx={cardSx}>
            <Box sx={{
              px: 2.5, py: 2, borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <BookmarkRoundedIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
              <Typography sx={{
                flex: 1, fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: 'text.disabled',
              }}>
                {t('dynamicReports.savedTitle')}
              </Typography>
              <Box sx={{
                px: 0.85, py: 0.2, borderRadius: 1, fontSize: 11, fontWeight: 800,
                bgcolor: alpha(theme.palette.text.primary, 0.06), color: 'text.secondary',
              }}>
                {saved.length}
              </Box>
            </Box>

            <Stack sx={{ p: 1 }}>
              {saved.map((r) => {
                const active = activeSaved === r.id;
                return (
                  <Box key={r.id}
                    onClick={() => handleRunSaved(r)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1.5, py: 1.25, borderRadius: 2, cursor: 'pointer',
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      transition: 'background-color 140ms ease',
                      '&:hover': {
                        bgcolor: active
                          ? alpha(theme.palette.primary.main, 0.12)
                          : alpha(theme.palette.text.primary, 0.04),
                      },
                      '&:hover .row-actions': { opacity: 1 },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: 13.5, fontWeight: 600, lineHeight: 1.3,
                        color: active ? 'primary.main' : 'text.primary',
                      }} noWrap>
                        {r.name}
                      </Typography>
                      {r.description && (
                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }} noWrap>
                          {r.description}
                        </Typography>
                      )}
                    </Box>
                    <Box className="row-actions" sx={{ opacity: 0, transition: 'opacity 140ms ease' }}>
                      <Tooltip title={t('dynamicReports.delete')}>
                        <IconButton size="small" color="error"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}>
                          <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        )}
      </Box>
      </Box>

      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={t('dynamicReports.deleteDialog.title')}
        itemName={deleteTarget?.name || ''}
        message={t('dynamicReports.deleteDialog.message', { name: deleteTarget?.name || '' })}
      />

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('dynamicReports.saveDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            {t('dynamicReports.saveDialog.hint')}
          </Typography>
          <TextField
            autoFocus fullWidth label={t('dynamicReports.saveDialog.nameLabel')}
            value={saveName} onChange={(e) => setSaveName(e.target.value)}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSaveOpen(false)} sx={{ textTransform: 'none' }}>
            {t('dynamicReports.cancel')}
          </Button>
          <Button variant="contained" disableElevation onClick={handleSave}
            disabled={!saveName.trim()}
            sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}>
            {t('dynamicReports.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default DynamicReportsPage;
